/**
 * Where to put marks on the time slider, given the frames it is showing.
 *
 * Plain TypeScript with no Svelte imports, like the rest of `lib/map/`, so the
 * rule can be asserted directly rather than through a mounted component.
 *
 * Two regimes, because one does not cover both ends of the range any more.
 * Below the threshold every frame gets a mark, which is what says how many
 * frames there are and where the thumb will land. Above it — a 73-step
 * sub-hourly HRRR run is now an ordinary case rather than an impossible one —
 * the per-frame marks merge into a grey bar that says nothing, so the marks
 * become a *scale* instead: the frames that land on a round interval, with the
 * ones that start a local day called out.
 *
 * The positions are frame indices, not instants, because the track is
 * index-spaced — the honest rendering of a strided or non-uniform axis, where
 * the gap between two frames is not the same everywhere.
 */

import { chooseInterval, MINUTE } from '../../time'

export interface Tick {
  /** Index into the frame list; the track is index-spaced. */
  index: number
  /** Position along the track, 0-100. */
  left: number
  /** The first frame of a local day. Drawn stronger; it is the useful cue. */
  day: boolean
}

/** Past this many frames, per-frame marks are a grey bar rather than a scale. */
export const MARK_EVERY_FRAME_UPTO = 20

/**
 * How many scale marks to aim for when the caller does not say.
 *
 * The real constraint is pixels between marks, which this module cannot see —
 * so the caller, which knows its layout, passes the number. `TimeSlider` asks
 * for 24 on a desktop track (~740 px, so ~30 px apart at worst) and 8 on a
 * phone's (~130 px). Measured on 2026-09-19: at 10, an 18-hour sub-hourly axis
 * fell through hourly to 3-hourly and drew seven marks across the full width,
 * which is a scale too coarse to place anything against.
 */
export const DEFAULT_TARGET_MARKS = 12

export function tickMarks(times: readonly Date[], targetMarks = DEFAULT_TARGET_MARKS): Tick[] {
  const count = times.length
  if (count < 2) return []

  const at = (index: number): Tick => ({
    index,
    left: (index / (count - 1)) * 100,
    day: index > 0 && times[index].getDate() !== times[index - 1].getDate(),
  })

  if (count <= MARK_EVERY_FRAME_UPTO) {
    return Array.from({ length: count }, (_, i) => at(i))
  }

  const span = times[count - 1].getTime() - times[0].getTime()
  // The interval table lives in `lib/time.ts` because the chart's axis marks
  // on the same round intervals. It cannot share this *function* — the track
  // here is index-spaced and an axis is time-spaced — but a second copy of the
  // table is the `GROUP_LABELS` bug again.
  const interval = chooseInterval(span, targetMarks)

  // A frame is on the scale when it is the first one at or after a boundary,
  // which is what keeps a strided axis marked at all: striding 73 frames to 48
  // leaves alternating 15- and 30-minute gaps, so testing for an exact
  // multiple would silently drop half the marks.
  const ticks: Tick[] = []
  let boundary = -Infinity
  for (let i = 0; i < count; i++) {
    const t = times[i].getTime()
    // Local boundaries, not UTC: the day marks have to line up with the labels
    // beside them, which `Intl` formats in the viewer's zone.
    const local = t - times[i].getTimezoneOffset() * MINUTE
    const floor = Math.floor(local / interval) * interval
    if (floor > boundary) {
      boundary = floor
      ticks.push(at(i))
    }
  }
  return ticks
}

/**
 * The frame under a point on the track, for a finger.
 *
 * iOS moves a native range only from its thumb: a tap on the groove does
 * nothing, and 1B's 14 px square is a small thing to land on. So a touch
 * anywhere on the track scrubs to the frame under it, the way a mouse click
 * on the groove already does in every desktop engine.
 *
 * A range input's thumb *centre* travels from half a thumb in from the left
 * edge to half a thumb in from the right, not edge to edge, and the marks are
 * inset by the same half thumb so they line up with it. This inverts that
 * mapping: `x` is from the input's left edge, in the same pixels as `width`
 * and `thumb`. Anything off either end clamps to the end frame.
 */
export function frameAt(x: number, width: number, thumb: number, count: number): number {
  if (count <= 1) return 0
  const travel = width - thumb
  if (travel <= 0) return 0
  const t = (x - thumb / 2) / travel
  return Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))))
}
